"use client";
import React from 'react';
import { User, Users, Baby, AlertTriangle, Globe, Filter, Search, X } from 'lucide-react';

const categories = [
  { id: 'all', label: 'All Causes', icon: Globe, count: 342 },
  { id: 'individual', label: 'Individual', icon: User, count: 128 },
  { id: 'family', label: 'Family', icon: Users, count: 94 },
  { id: 'child', label: 'Child', icon: Baby, count: 87 },
  { id: 'emergency', label: 'Emergency', icon: AlertTriangle, count: 33 },
];

const urgencyLevels = [
  { id: 'all', label: 'Any Urgency' },
  { id: 'critical', label: 'Critical' },
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
];

const locations = [
  'All UK', 'London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow', 'Liverpool', 'Bristol', 'Sheffield'
];

const sortOptions = [
  { id: 'urgent', label: 'Most Urgent First' },
  { id: 'goal', label: 'Nearest to Goal' },
  { id: 'recent', label: 'Recently Added' },
  { id: 'donors', label: 'Most Donors' },
];

export interface FilterState {
  search: string;
  category: string;
  urgency: string;
  location: string;
  sort: string;
}

interface CategoryFilterProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
}

export default function CategoryFilter({ filters, onFiltersChange, showFilters, onToggleFilters }: CategoryFilterProps) {
  const update = (key: keyof FilterState, value: string) =>
    onFiltersChange({ ...filters, [key]: value });

  const hasActiveFilters =
    filters.search !== '' ||
    filters.category !== 'all' ||
    filters.urgency !== 'all' ||
    filters.location !== 'All UK' ||
    filters.sort !== 'urgent';

  const clearAll = () =>
    onFiltersChange({ search: '', category: 'all', urgency: 'all', location: 'All UK', sort: 'urgent' });

  return (
    <div className="mb-8" id="causes">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-700 text-gray-900">Browse Causes</h2>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={clearAll}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-all duration-150"
            >
              <X size={13} />
              Clear filters
            </button>
          )}
          <button
            onClick={onToggleFilters}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border transition-all duration-150 ${showFilters ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >
            <Filter size={15} />
            Filters
            {hasActiveFilters && (
              <span className="w-2 h-2 rounded-full bg-teal-500 inline-block" />
            )}
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Search causes by title, charity, or keyword…"
          value={filters.search}
          onChange={(e) => update('search', e.target.value)}
          className="w-full pl-10 pr-10 py-3 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 placeholder-gray-400 transition-all duration-150"
        />
        {filters.search && (
          <button
            onClick={() => update('search', '')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Category Pills */}
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2">
        {categories?.map((cat) => (
          <button
            key={`cat-${cat?.id}`}
            onClick={() => update('category', cat?.id)}
            className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-150 ${
              filters.category === cat?.id
                ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300 hover:text-teal-600'
            }`}
          >
            <cat.icon size={15} />
            {cat?.label}
            <span className={`px-1.5 py-0.5 rounded-full text-xs ${filters.category === cat?.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
              {cat?.count}
            </span>
          </button>
        ))}
      </div>

      {/* Expanded Filters Panel */}
      {showFilters && (
        <div className="mt-4 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Urgency Level</p>
              <div className="flex flex-wrap gap-2">
                {urgencyLevels?.map((u) => (
                  <button
                    key={`urgency-${u?.id}`}
                    onClick={() => update('urgency', u?.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-150 ${
                      filters.urgency === u?.id
                        ? 'bg-amber-50 text-amber-700 border-amber-300' :'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {u?.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Location</p>
              <div className="flex flex-wrap gap-2">
                {locations?.map((loc) => (
                  <button
                    key={`loc-${loc}`}
                    onClick={() => update('location', loc)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-150 ${
                      filters.location === loc
                        ? 'bg-teal-50 text-teal-700 border-teal-300' :'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {loc}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Sort By</p>
              <select
                value={filters.sort}
                onChange={(e) => update('sort', e.target.value)}
                className="input-field text-sm w-full"
              >
                {sortOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}